<script setup lang="ts">

import {useCommunityDataPublic} from '@/stores/communityDataPublic'
import {useCommunityApiStore} from '@/stores/communityApiStore'
import type { FormEnergyCommunity } from '@/stores/communityApiStore'
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import {useFormDataStore} from "@/stores/formDataStore";
import {useRoute, useRouter, onBeforeRouteLeave, onBeforeRouteUpdate} from "vue-router";
import MemberJoinStepperMasterData from "@/components/MemberJoinStepperMasterData.vue";
import MemberJoinStepperMeteringPoints from "@/components/MemberJoinStepperMeteringPoints.vue";
import MemberJoinStepperBillingData from "@/components/MemberJoinStepperBillingData.vue";
import MemberJoinStepperSummaryConsent from "@/components/MemberJoinStepperSummaryConsent.vue";
import {useDataProcessing} from "@/lib/dataProcessing"
import {
  Stepper,
  PrimaryButton,
  TertiaryButton,
  useSnackbarStore,
  SnackbarEnum,
  SafeHtml
} from '@es-bill/es-bill-common-components';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify'

const { t } = useI18n();
const formDataStore = useFormDataStore();
const {processAndSubmitData} = useDataProcessing(t);
const router = useRouter()
const route = useRoute()
const { width } = useDisplay()

const tinyScreen = computed(() => width.value < 440);

// Access $appconfig through getCurrentInstance()
const instance = getCurrentInstance();
const ac = instance?.appContext.config.globalProperties.$appconfig;
const snackbarStore = useSnackbarStore();

const communityDataPublicStore = useCommunityDataPublic()
const communityApiStore = useCommunityApiStore()
const communityPublic = computed(()=> communityDataPublicStore.communityActive);
const multiDsoMode = computed(()=> communityPublic.value?.communityType === '16b');

// Loading state for component
const isLoadingCommunity = ref(false);
const hasAttemptedLoad = ref(false);

onMounted(async () => {
  // Only process if we have an ECN in the route
  const ecn = route.params.ecn as string;

  if (ecn) {
    // If community is not loaded yet, try to load it
    if (!communityPublic.value && !hasAttemptedLoad.value) {
      isLoadingCommunity.value = true;
      hasAttemptedLoad.value = true;

      try {
        // Fetch community by ECN
        const fetchedCommunity = await communityApiStore.fetchCommunityByEcn(ecn);
        if (fetchedCommunity) {
          communityDataPublicStore.setCommunityActive(fetchedCommunity);
        } else {
          snackbarStore.showSnackbar(t("unexpectedError"), t("communityNotFound"), SnackbarEnum.Warning, 5000, [])
          router.push({name: 'HomeView', replace: true});
        }
      } catch (error) {
        console.error("Error loading community:", error);
        snackbarStore.showSnackbar(t("unexpectedError"), t("communityNotFound"), SnackbarEnum.Warning, 5000, [])
        router.push({name: 'HomeView', replace: true});
      } finally {
        isLoadingCommunity.value = false;
      }
    }
  } else {
    console.info("No ECN provided, redirecting to home");
    snackbarStore.showSnackbar(t("unexpectedError"), t("communityNotFound"), SnackbarEnum.Warning, 5000, [])
    router.push({name: 'HomeView', replace: true});
  }

  // console.warn("route.params.step",route.params.step)
  // Ensure URL has step parameter for consistency > direct calls without step are very common
  if (route.params.step.length === 0) {
    await router.replace({
      name: 'JoinAsMember',
      params: {
        ecn: route.params.ecn,
        step: '1'
      }
    })

  } else  {
    // direct loading of page with step set, with is not 1
    // also we use this on nav back from the finish page to come back to the first form page/step

    // check if we have already started with entering anything
    // lookup the first thing with needs to be entered
    if(route.params.step !== "1" && formDataStore.formData.isCompany.length === 0) {
      await router.replace({
        name: 'JoinAsMember',
        params: {
          ecn: route.params.ecn,
          step: '1'
        }
      })
    }
  }
})


// Watch for community changes after loading
watch(communityPublic, (newCommunity: FormEnergyCommunity | null) => {
  if (hasAttemptedLoad.value && !isLoadingCommunity.value && !newCommunity) {
    snackbarStore.showSnackbar(t("unexpectedError"), t("communityNotFound"), SnackbarEnum.Warning, 5000, [])
    router.push({name: 'HomeView', replace: true});
  }
});

// Single source of truth - step comes from URL
// fallback correctly otherwise direct calls to /join/:ecn do not work
const step = computed(() => Number(route.params.step || 1)); // > also

// Type the form refs to include formValidates method
interface FormWithValidation {
  formValidates: () => Promise<boolean>;
  showContent?: boolean; // Add optional showContent property
  showNoMeteringPointsDialog?: () => Promise<boolean>;
}
const form1 = ref<FormWithValidation | null>(null);
const form2 = ref<FormWithValidation | null>(null);
const form3 = ref<FormWithValidation | null>(null);
const form4 = ref<FormWithValidation | null>(null);

// Flag to track if navigation is programmatic from next() - prevents duplicate validation
const isNavigatingFromNext = ref(false);

// Simple navigation function
function goToStep(n: number) {
  router.push({
    name: 'JoinAsMember',
    params: {
      ecn: route.params.ecn,
      step: String(n)
    }
  });
}

// Handle route changes with navigation guards
onBeforeRouteUpdate(async (to: any, from: any) => {
  const toStep = Number(to.params.step || 1); // correct fallback
  const fromStep = Number(from.params.step || 1); // correct fallback

  // Skip validation if navigation is from next() - it already validated
  if (isNavigatingFromNext.value) {
    isNavigatingFromNext.value = false; // Reset flag
    return; // Allow navigation, validation was already done
  }

  // Validate when going forward (for direct URL navigation, browser back/forward, etc.)
  if (toStep > fromStep) {
    let isValid = false;

    // Validate the step we're leaving
    if (fromStep === 1 && form1.value) {
      isValid = await form1.value.formValidates();
    } else if (fromStep === 2 && form2.value) {
      isValid = await form2.value.formValidates();
      if (isValid && form2.value.showNoMeteringPointsDialog) {
        const shouldContinue = await form2.value.showNoMeteringPointsDialog();
        if (!shouldContinue) {
          isValid = false;
        }
      }
    } else if (fromStep === 3 && form3.value) {
      isValid = await form3.value.formValidates();
    }

    if (!isValid) {
      return false; // Block navigation
    }
  }

  // Allow backward navigation (no validation needed)
});

// Handle leaving the page entirely
onBeforeRouteLeave(() => {

  // @todo what is this for? this blocks viewing FinishedApplication after submit
  // if (step.value > 1) {
  //   goToStep(step.value - 1);
  //   return false; // Stay on the page; URL restored
  // }
});

// Computed property to check if buttons should be shown based on step 1 selection
const shouldShowButtons = computed(() => {
  if (step.value === 1) {
    return form1.value?.showContent === true;
  }
  return true;
});

const steps = [
  { key: 'step1', text: t('memberJoinStepper.masterData') },
  { key: 'step2', text: t('memberJoinStepper.meteringPoints') },
  { key: 'step3', text: t('memberJoinStepper.billingData') },
  { key: 'step4', text: t('memberJoinStepper.summary') }
];

async function next() {
  // Validate current step first
  const currentStep = step.value;
  let isValid = false;

  if (currentStep === 1 && form1.value) {
    isValid = await form1.value.formValidates();
  } else if (currentStep === 2 && form2.value) {
    isValid = await form2.value.formValidates();
    // Also check metering points dialog
    if (isValid && form2.value.showNoMeteringPointsDialog) {
      const shouldContinue = await form2.value.showNoMeteringPointsDialog();
      if (!shouldContinue) {
        isValid = false;
      }
    }
  } else if (currentStep === 3 && form3.value) {
    isValid = await form3.value.formValidates();
  }

  // Only move if valid
  if (isValid) {
    isNavigatingFromNext.value = true; // Set flag before navigation to skip duplicate validation
    goToStep(step.value + 1);
  }
}

function prev() {
  // goToStep(Math.max(1, step.value - 1))
  router.back() // use browser history (otherwise back in browser then brings us back to the origin page)
}

function handleStepChange(newStepIndex: number) {
  const newStep = newStepIndex + 1;
  goToStep(newStep);
}

async function checkSubmit() {
  if(step.value === 4 && form4.value && (await form4.value.formValidates()) === false)
    return;

  overlayActive.value=true;
  let errorMessage = "";

  // validation (check previous forms, can happen during reloads with updated formdata store)
  if(form1.value && form2.value && form3.value && // @todo this is pointless since form[1,2,3] are not set
    ((await form1.value.formValidates()) === false || (await form2.value.formValidates()) === false || (await form3.value.formValidates()) === false)) {
    errorMessage = t('formReloadRequired');
  }

  if(errorMessage.length === 0 && ac) {
    const result = await processAndSubmitData(ac as any);
    if (typeof result === 'string') {
      errorMessage = result;
    }
  }

  if(errorMessage.length) {
    overlayActive.value=false;
    overlayErrorMessage.value=errorMessage;
    overlayErrorActive.value=true
    return;
  }

  await router.push({ name: 'FinishedApplication', replace: false});
}

const overlayActive = ref(false);
const overlayErrorActive = ref(false);
const overlayErrorMessage = ref("");

</script>

<script lang="ts">
export default {
  name: 'JoinAsMember'
}
</script>

<template>
  <v-container class="pa-0 mb-10">

    <!-- Loading indicator for community data -->
    <div v-if="isLoadingCommunity" class="d-flex justify-center align-center pa-8">
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
      ></v-progress-circular>
    </div>

    <!-- Main content - only show when not loading -->
    <div v-else-if="communityPublic">

    <v-overlay v-model="overlayActive"
               activator="vApp"
               scroll-strategy="block"
               class="align-center justify-center"
               persistent
    >
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
    </v-overlay>

    <v-dialog
      v-model="overlayErrorActive"
      width="auto"
    >
      <v-card
        max-width="400"
        :title="t('unexpectedError')"
      >
        <v-card-text>
          <SafeHtml :html="overlayErrorMessage" />
        </v-card-text>
        <template v-slot:actions>
          <v-btn
            class="ms-auto"
            :text="t('ok')"
            @click="overlayErrorActive = false"
          ></v-btn>
        </template>
      </v-card>
    </v-dialog>



    <div class="bg-background-warm py-4" v-if="!tinyScreen" >
      <p class="textHeadline" >{{ t('becomeCommunityMember') }}</p>
      <p class="textDescriptor">{{communityPublic?.name}} | {{communityPublic?.ecn}}</p>
    </div>

    <div class="bg-background-warm py-4" v-if="tinyScreen">
      <p class="textHeadline" >{{ t('becomeMember') }}</p>
      <p class="textDescriptor">{{communityPublic?.name}} <br> {{communityPublic?.ecn}}</p>
    </div>
    <Stepper
      class="bg-background-warm"
      :steps="steps"
      :current-step="step"
      :allow-previous-navigation="true"
      @step-change="handleStepChange"
    >
      <template #step-1>
        <div class="bg-white">
          <MemberJoinStepperMasterData ref="form1" :community-public="communityPublic"/>
        </div>
      </template>

      <template #step-2>
        <div class="bg-white">
          <MemberJoinStepperMeteringPoints ref="form2" :community-public="communityPublic"/>
        </div>
      </template>

      <template #step-3>
        <div class="bg-white">
          <MemberJoinStepperBillingData ref="form3" :community-public="communityPublic"
              :isActive="(step === 3)"/>
        </div>
      </template>

      <template #step-4>
        <div class="bg-white">
          <MemberJoinStepperSummaryConsent ref="form4" :community-public="communityPublic"
              :isActive="(step === 4)"/>
        </div>
      </template>
    </Stepper>

    <!-- Use v-if with shouldShowButtons computed property to conditionally show buttons -->
  <v-fade-transition>
    <div v-if="shouldShowButtons">
      <div class="d-flex flex-md-row flex-column align-center pa-4" :class="step !== 2 ? 'justify-end' : 'justify-space-between'">
        <TertiaryButton
          color="black"
          v-if="step === 2"
          prepend-icon="mdi-plus"
          @click="formDataStore.addMeteringPoint()"
          :text="t('memberJoinStepper.addMeteringPoint')"
          class="full-width-mobile"
        />
        <div class="d-flex flex-md-row flex-column justify-end full-width-mobile">
        <TertiaryButton
               color="black"
               @click="prev()"
               :disabled="step===1"
               :text="t('memberJoinStepper.back')"
               class="mb-2 mb-md-0 full-width-mobile"
        />
        <PrimaryButton
               ref="elementButtonNext"
               v-if="step !== 4"
               variant="flat"
               append-icon="mdi-arrow-right"
               @click="next()"
               :text="t('memberJoinStepper.next')"
               class="ml-md-12 ml-0 full-width-mobile"
        />
        <PrimaryButton
               ref="elementButtonApply"
               v-if="step === 4"
               variant="flat"
               append-icon="mdi-arrow-right"
               @click="checkSubmit()"
               :disabled="!communityPublic"
               :text="t('memberJoinStepper.apply')"
               class="ml-md-12 ml-0 full-width-mobile"
       />
       </div>
      </div>
    </div>
  </v-fade-transition>

    </div> <!-- Close conditional div for communityPublic -->
  </v-container>


</template>

<style scoped lang="scss">

.containerStandard {
  padding: 0;
}

.containerIntro {
  margin-top: 0;
  margin-left: 0;
  width: 100%;
}

@media (max-width: 960px) {
  .full-width-mobile {
    width: 100%;
  }
}
.custom-stepper {
  background-color: rgb(var(--v-theme-background-warm)) !important;
  :deep(.v-stepper-header) {
    background-color: white !important;
  }
}
</style>
